import { Grid, Stack } from '@mui/material'
import FormInput from 'components/FormInput'
import { FormInputSelect, ItemOption } from 'components/FormInputSelect'
import React, { useCallback, useEffect, useState } from 'react'
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import genericService from 'services/generic.service'
import { HearingIdentificationType } from 'types/hearing-identification.type'
import { useHearingIdentificationBlockFieldsContext } from '../../../../../../contexts/hearing-identification-block-fields.context'

type ResponsibleRelationshipGroupProps = {
  control: Control<HearingIdentificationType>
  watch: UseFormWatch<HearingIdentificationType>
  setValue: UseFormSetValue<HearingIdentificationType>
}

const LIVING_WITH_OTHER_OPTION_ID = '38'
const DO_KEEP_IN_TOUCH_OPTION_ID: string[] = ['S', 'E']

const choiceOptions: ItemOption[] = [
  { label: 'Sim', value: 'S' },
  { label: 'Não', value: 'N' },
  { label: 'Eventualmente', value: 'E' },
]

const choiceReasonNoContactWithMotherAndFatherOptions: ItemOption[] = [
  { label: 'Sim', value: 'S' },
  { label: 'Não', value: 'N' },
  { label: 'Eventualmente', value: 'E' },
  { label: 'Falecido', value: 'F' },
]

export const ResponsibleRelationshipGroup: React.FC<ResponsibleRelationshipGroupProps> = ({
  control,
  watch,
  setValue,
}) => {
  const { isBlockedFields } = useHearingIdentificationBlockFieldsContext()

  const [livingWithOptions, setLivingWithOptions] = useState<ItemOption[]>([])

  const livingWithIdValue = watch('identificacaoAdolescente.idResidenciaResponsavel')
  const keepInTouchMotherIdValue = watch('identificacaoAdolescente.mantemContatoMae')
  const keepInTouchFatherIdValue = watch('identificacaoAdolescente.mantemContatoPai')

  const isLivingWithOther = String(livingWithIdValue) === LIVING_WITH_OTHER_OPTION_ID
  const isKeepingInTouchMother = DO_KEEP_IN_TOUCH_OPTION_ID.includes(
    String(keepInTouchMotherIdValue),
  )
  const isKeepingInTouchFather = DO_KEEP_IN_TOUCH_OPTION_ID.includes(
    String(keepInTouchFatherIdValue),
  )

  const fillLivingWithOptions = useCallback(async (): Promise<void> => {
    await genericService
      .get('/residencia-responsavel')
      .then((res) => res && setLivingWithOptions(res.data))
      .catch((error) => console.error('error', error))
  }, [setLivingWithOptions])

  useEffect(() => {
    fillLivingWithOptions()
  }, [fillLivingWithOptions])

  useEffect(() => {
    if (!isLivingWithOther) {
      setValue('identificacaoAdolescente.nomeResponsavelResidenciaOutro', '')
    }
  }, [isLivingWithOther])

  useEffect(() => {
    if (isKeepingInTouchMother) {
      setValue('identificacaoAdolescente.motivoNaoMantemContatoMae', '')
    }
  }, [isKeepingInTouchMother])

  useEffect(() => {
    if (isKeepingInTouchFather) {
      setValue('identificacaoAdolescente.motivoNaoMantemContatoPai', '')
    }
  }, [isKeepingInTouchFather])

  return (
    <>
      <Grid item md={isLivingWithOther ? 4 : 6} xs={12}>
        <FormInputSelect
          control={control}
          name='identificacaoAdolescente.recebePensaoAlimenticia'
          label='Recebe pensão alimentícia ou outro auxílio do representante legal?'
          options={choiceOptions}
          disabled={isBlockedFields}
        />
      </Grid>

      <Grid item md={isLivingWithOther ? 4 : 6} xs={12}>
        <FormInputSelect
          control={control}
          name='identificacaoAdolescente.idResidenciaResponsavel'
          label='Reside com quem?'
          options={livingWithOptions}
          disabled={isBlockedFields}
        />
      </Grid>
      {isLivingWithOther && (
        <Grid item xs>
          <FormInput
            control={control}
            name='identificacaoAdolescente.nomeResponsavelResidenciaOutro'
            label='Quem?'
            maxLength={60}
            placeholder='Informe com quem reside'
          />
        </Grid>
      )}

      <Grid item md={6} xs={12}>
        <Stack direction={'column'} spacing={1}>
          <FormInputSelect
            control={control}
            name='identificacaoAdolescente.mantemContatoMae'
            label='Adolescente mantém contato com a mãe?'
            options={choiceReasonNoContactWithMotherAndFatherOptions}
            disabled={isBlockedFields}
          />
          {keepInTouchMotherIdValue === 'N' && (
            <FormInput
              control={control}
              name='identificacaoAdolescente.motivoNaoMantemContatoMae'
              label='Motivo:'
              maxLength={250}
              multiline
              minRows={4}
              placeholder='Informe o motivo'
            />
          )}
        </Stack>
      </Grid>
      <Grid item md={6} xs={12}>
        <Stack direction={'column'} spacing={1}>
          <FormInputSelect
            control={control}
            name='identificacaoAdolescente.mantemContatoPai'
            label='Adolescente mantém contato com o pai?'
            options={choiceReasonNoContactWithMotherAndFatherOptions}
            disabled={isBlockedFields}
          />
          {keepInTouchFatherIdValue === 'N' && (
            <FormInput
              control={control}
              name='identificacaoAdolescente.motivoNaoMantemContatoPai'
              label='Motivo:'
              maxLength={250}
              multiline
              minRows={4}
              placeholder='Informe o motivo'
            />
          )}
        </Stack>
      </Grid>
    </>
  )
}
